import React, { FormEvent, useCallback, useEffect, useMemo } from 'react'

import { useIsTextTruncated } from '@inno-tools/react-hooks'
import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import { Checkbox, CheckboxSizes } from '@inno-ui/checkbox'
import { Chip, ChipSizes } from '@inno-ui/chip'
import {
  ArrowCirclePathLeftIcon,
  CircleCheckmarkLineIcon,
  IconSizes,
  IconWeight,
  LockFullIcon,
  PersonFilledIcon
} from '@inno-ui/icons'
import { TextInput, TextInputSizes } from '@inno-ui/text-input'
import { toast } from '@inno-ui/toast'
import { Tooltip, TooltipPlacements } from '@inno-ui/tooltip'
import { AnyFieldApi, useForm, useStore } from '@tanstack/react-form'
import cn from 'classnames'
import { Trans, useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import {
  useSetPasswordMutation,
  useValidateTokenMutation
} from 'shared/api/auth/requests'
import { useLoginMutation } from 'shared/api/auth/requests/login'
import setPasswordBg from 'shared/assets/backgrounds/set-password-bg.webp'
import { PasswordRequirements } from 'shared/components/PasswordRequirements'
import { DEFAULT_REMEMBER_ME } from 'shared/constants/user'
import { useSearchParams } from 'shared/hooks/useSearchParams'

import { AuthPageTemplate } from '../../templates'
import styles from './SetPasswordPage.module.scss'
import { getSetPasswordSchema } from './SetPasswordPage.schema'
import { ContentWrapper } from './components'

export const SetPasswordPage = () => {
  const { t, i18n } = useTranslation('auth', {
    keyPrefix: 'set_password'
  })
  const currentLocale = i18n?.resolvedLanguage ?? i18n?.language ?? 'en'
  const landingDomain = process.env.REACT_APP_LANDING_DOMAIN
  const [{ token }] = useSearchParams<{ token?: string }>()

  const termsOfServiceUrl = `${landingDomain}/terms-and-conditions?l=${currentLocale}`
  const privacyPolicyUrl = `${landingDomain}/privacy-policy?l=${currentLocale}`

  const handleLinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation()
    },
    []
  )

  const setPasswordSchema = useMemo(() => getSetPasswordSchema(t), [t])

  const { isTruncated, textRef: emailRef } = useIsTextTruncated()

  const validateTokenMutation = useValidateTokenMutation({
    onError: () => {
      toast.error({
        title: t('error.expired_title'),
        subTitle: t('error.expired_subTitle')
      })
    }
  })
  const setPasswordMutation = useSetPasswordMutation({
    onError: error => {
      if (error.response?.status === 401) {
        toast.error({
          title: t('error.expired_title'),
          subTitle: t('error.expired_subTitle')
        })
      }
    }
  })
  const loginMutation = useLoginMutation()

  useEffect(() => {
    if (token && validateTokenMutation.isIdle) {
      validateTokenMutation.mutate({ token })
    }
  }, [token, validateTokenMutation])

  const form = useForm({
    defaultValues: {
      password: '',
      repeatPassword: '',
      terms: false
    },
    validators: {
      onMount: setPasswordSchema,
      onChange: setPasswordSchema
    },
    onSubmit: ({ value }) => {
      setPasswordMutation.mutate({
        password: value.password,
        repeat_password: value.repeatPassword,
        token: token!
      })
    }
  })

  const canSubmit = useStore(form.store, state => state.canSubmit)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    form.handleSubmit()
  }

  if (!token || validateTokenMutation.isError) {
    return <Navigate to='/' replace />
  }

  const getFieldError = (field: AnyFieldApi) => {
    const errors = field.state.meta.errors
    const isTouched = field.state.meta.isTouched
    return errors.length > 0 && isTouched
      ? errors.map(error => error?.message).join(', ')
      : undefined
  }

  return (
    <AuthPageTemplate
      className={styles.wrapper}
      backgroundImageUrl={setPasswordBg}
      showHeader={false}
    >
      <div className={styles.setPasswordPage}>
        {setPasswordMutation.isSuccess ? (
          <ContentWrapper>
            <div className={styles.successContent}>
              <CircleCheckmarkLineIcon
                className={styles.icon}
                color='var(--warning-green-light)'
              />
              <h1 className={styles.title}>{t('success.title')}</h1>
              <div className={styles.buttonWrapper}>
                <Button
                  color={ButtonColors.Green}
                  variant={ButtonVariants.Dark}
                  size={ButtonSizes.L}
                  label={t('success.button')}
                  width='258px'
                  disabled={loginMutation.isPending}
                  onClick={() => {
                    loginMutation.mutate({
                      email: validateTokenMutation.data!.data.email,
                      password: form.state.values.password,
                      remember_me: DEFAULT_REMEMBER_ME
                    })
                  }}
                />
              </div>
            </div>
          </ContentWrapper>
        ) : (
          <ContentWrapper
            isLoading={
              setPasswordMutation.isPending || validateTokenMutation.isPending
            }
          >
            <div className={styles.content}>
              <div className={styles.header}>
                <h1 className={styles.title}>{t('title')}</h1>
                <p className={styles.description}>{t('description')}</p>
                <div className={styles.accountPreview}>
                  <div className={styles.email}>
                    <PersonFilledIcon
                      size={IconSizes.Size16}
                      weight={IconWeight.Bold}
                      color='var(--grey-500)'
                      className={styles.emailIcon}
                    />
                    <Tooltip
                      overlay={validateTokenMutation.data?.data.email}
                      placement={TooltipPlacements.Bottom}
                      disabled={!isTruncated}
                    >
                      <span className={styles.emailText} ref={emailRef}>
                        {validateTokenMutation.data?.data.email}
                      </span>
                    </Tooltip>
                  </div>
                  <div className={styles.accountType}>
                    <Chip
                      label={
                        validateTokenMutation.data?.data.is_personal_account
                          ? t('account_preview.personal_account')
                          : t('account_preview.customer_account')
                      }
                      bgcolor='var(--secondary-blue-light)'
                      color='var(--white)'
                      size={ChipSizes.S}
                    />
                  </div>
                </div>
              </div>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.group}>
                  <img
                    src={`${process.env.REACT_APP_UIKIT_CDN_URL}/illustrations/man-with-lock.png`}
                    alt='Man with lock illustration'
                    className={styles.illustration}
                  />
                  <form.Field name='password'>
                    {field => {
                      const error = getFieldError(field)
                      return (
                        <>
                          <TextInput
                            placeholder={t('form.set_password_placeholder')}
                            type='password'
                            size={TextInputSizes.L}
                            icon={LockFullIcon}
                            value={field.state.value}
                            onChange={e => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            hasError={!!error}
                          />
                          <PasswordRequirements password={field.state.value} />
                        </>
                      )
                    }}
                  </form.Field>
                </div>
                <div className={styles.group}>
                  <form.Field name='repeatPassword'>
                    {field => {
                      const error = getFieldError(field)
                      return (
                        <TextInput
                          placeholder={t('form.repeat_password_placeholder')}
                          type='password'
                          size={TextInputSizes.L}
                          icon={ArrowCirclePathLeftIcon}
                          value={field.state.value}
                          onChange={e => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          errorText={error}
                          hasError={!!error}
                        />
                      )
                    }}
                  </form.Field>
                </div>
                <div className={styles.group}>
                  <form.Field name='terms'>
                    {field => {
                      const error = getFieldError(field)
                      return (
                        <div className={styles.terms}>
                          <Checkbox
                            size={CheckboxSizes.L}
                            checked={field.state.value}
                            onChange={field.handleChange}
                            hasError={!!error}
                          />
                          <span
                            className={cn(styles.termsLabel, {
                              [styles.termsLabelError]: !!error
                            })}
                            onClick={() =>
                              field.handleChange(!field.state.value)
                            }
                          >
                            <Trans
                              ns='auth'
                              i18nKey='set_password.form.accept_terms_label'
                              components={{
                                terms: (
                                  <a
                                    href={termsOfServiceUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={handleLinkClick}
                                    className={styles.link}
                                  />
                                ),
                                privacy: (
                                  <a
                                    href={privacyPolicyUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={handleLinkClick}
                                    className={styles.link}
                                  />
                                )
                              }}
                            />
                          </span>
                        </div>
                      )
                    }}
                  </form.Field>
                </div>
                <Button
                  label={t('form.set_password_button')}
                  size={ButtonSizes.L}
                  type='submit'
                  disabled={!canSubmit}
                />
              </form>
            </div>
          </ContentWrapper>
        )}
      </div>
    </AuthPageTemplate>
  )
}
